/** @format */

import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";

import { lazyWithRetry } from "@/utils/lazyWithRetry";
import PageLoader from "@/utils/PageLoader";
import { RouteErrorComponent } from "@/utils/RouteErrorComponent";
import { fetchRacesBySeriesAndYear, yearSearchSchema } from "@/utils/routeUtils";

const SeriesSchedulePage = lazyWithRetry(() => import("@/pages/Series/SeriesSchedule"));

export const Route = createFileRoute("/series/$series/schedule")({
  validateSearch: yearSearchSchema,
  loaderDeps: ({ search }) => ({ year: search.year }),
  /**
   * Loader prefetches data for instant rendering
   *
   * NOTE: The component also uses useRacesList() hook.
   * This is INTENTIONAL and follows the recommended TanStack Router + React Query pattern:
   *
   * - Loader prefetch = Instant render (no loading spinner)
   * - Component hook = Reactive updates + type-safe data access
   *
   * React Query's cache deduplication prevents duplicate network requests.
   * fetchRacesBySeriesAndYear uses raceQueryOptions.list internally (single source of truth).
   */
  loader: async ({ context, params, deps }) => {
    const { queryClient } = context;
    const { series } = params;
    const year = String(deps.year ?? new Date().getFullYear());

    // Prefetch schedule data using shared query options factory
    await fetchRacesBySeriesAndYear(queryClient, series, year);
  },
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <SeriesSchedulePage />
    </Suspense>
  ),
  errorComponent: RouteErrorComponent,
});
